import React, { useCallback, memo, useState } from 'react';
import * as Styled from './styled/CustomDateRange.styled';
import {
  InputWithMask,
  RadioGroup,
} from '@leasequery/lq.react-component-library';
import { SelectOption } from '@leasequery/lq.react-component-library/dist/models';
import { validate, ValidateConstraints } from '../../../../util';
import { INPUT_MASK } from '../../../../constants';
import { DateRangeMode, DATE_RANGE_MODE } from '../date-range/DateRange';
import {
  CustomCalendarPeriods,
  ValidCustomCalendarPeriods,
} from '../../../../models';
import { Icon } from '../../../../components';
import { useAuth } from '../../../../context';
import { getSingleDateComponentFormatConfig, isValidDateByFormat, parseLocaleSpecificDateStringToDefaultFormat } from '../../../../util/dateHelpers';
import { ReportSettingEndDateOption } from '../../../../models/reportTypeSetting';

interface CustomDateRangeProps {
  startDateMode?: DateRangeMode;
  endDateMode?: DateRangeMode;
  customCalendarMode?: DateRangeMode;
  shortStartDate: string; // TODO: change name to startDate since we allow fulldate now
  shortEndDate: string; // TODO: change name to endDate since we allow fulldate now
  customCalendarPeriods?: ValidCustomCalendarPeriods;
  selectedEndDateOption: string;
  endDateList: Array<SelectOption<string>>;
  onChange: (
    shortStartDate: string,
    shortEndDate: string,
    selectedEndDateOption: string,
    isValid: boolean,
  ) => void;
}

const createDateValidationConstraints = (
  inputMask: (string | RegExp)[],
  customCalendarPeriods?: ValidCustomCalendarPeriods,
): ValidateConstraints =>
  !!customCalendarPeriods
    ? CustomCalendarPeriods[customCalendarPeriods]
    : inputMask === INPUT_MASK.SHORT_DATE
    ? { shortDate: 'Invalid start date' }
    : inputMask === INPUT_MASK.FULL_DATE
    ? { fullDate: 'Invalid start date' }
    : {};

function CustomDateRangeComponent({
  startDateMode,
  endDateMode,
  customCalendarMode,
  shortStartDate,
  shortEndDate,
  selectedEndDateOption,
  endDateList,
  onChange,
  customCalendarPeriods,
}: CustomDateRangeProps): JSX.Element {
  const startDateConfig = DATE_RANGE_MODE[startDateMode];
  const customCalendarConfig = DATE_RANGE_MODE[customCalendarMode];
  const [isEndDateValid, setIsEndDateValid] = useState(true);

  const { user } = useAuth();

  const {
    dateFormat,
    placeHolder,
    inputMask,
    formattedDate: formattedEndDate
  } = getSingleDateComponentFormatConfig(
    user.localePreference,
    endDateMode,
    shortEndDate,
    isEndDateValid,
  );

  const inputHandler = useCallback(
    (
      shortStartDate: string,
      shortEndDate: string,
      selectedEndDateOption: string,
    ) => {
      let defaultFormatEndDate = shortEndDate; // Date value in default format for the input validation and saving

      if (endDateMode === DateRangeMode.FullDate && selectedEndDateOption === ReportSettingEndDateOption.CustomEndDate) {
        const isEndDateValid = isValidDateByFormat(shortEndDate, dateFormat);
        setIsEndDateValid(isEndDateValid);

        if (!isEndDateValid) {
          onChange(shortStartDate, defaultFormatEndDate, selectedEndDateOption, false);
          return;
        }
        
        defaultFormatEndDate = parseLocaleSpecificDateStringToDefaultFormat(shortEndDate, user.localePreference);
      }

      const startDateValidationConstraints = createDateValidationConstraints(
        startDateConfig.inputMask,
        customCalendarPeriods,
      );
      const endDateValidationConstraints = createDateValidationConstraints(
        inputMask,
      );

      let isValid =
        validate(shortStartDate, startDateValidationConstraints) === '';

      if (selectedEndDateOption === ReportSettingEndDateOption.CustomEndDate) {
        isValid =
          isValid &&
          validate(defaultFormatEndDate, endDateValidationConstraints) === '';
      }
      onChange(shortStartDate, defaultFormatEndDate, selectedEndDateOption, isValid);
    },
    [endDateMode, startDateConfig.inputMask, customCalendarPeriods, inputMask, onChange, dateFormat, user.localePreference],
  );
  return (
    <Styled.CustomDateRange>
      <div>
        <Icon name="calendar" />
      </div>
      <InputWithMask
        placeholder={
          customCalendarPeriods
            ? customCalendarConfig.placeHolder
            : startDateConfig.placeHolder
        }
        required={true}
        name="startDate"
        value={shortStartDate}
        onChange={useCallback(
          (event): void => {
            inputHandler(
              event.target.value.trim(),
              formattedEndDate,
              selectedEndDateOption,
            );
          },
          [inputHandler, formattedEndDate, selectedEndDateOption],
        )}
        mask={
          customCalendarPeriods
            ? customCalendarConfig.inputMask
            : startDateConfig.inputMask
        }
        label="Start Date"
      />
      <div>
        <RadioGroup
          options={endDateList}
          onChange={useCallback(
            (value): void => {
              inputHandler(shortStartDate, formattedEndDate, value);
            },
            [shortStartDate, formattedEndDate, inputHandler],
          )}
          name="endDate"
          selectedValue={selectedEndDateOption}
        />
        <div className="endDate">
          <InputWithMask
            placeholder={placeHolder}
            required={true}
            name="endDate"
            value={formattedEndDate}
            onChange={useCallback(
              (event): void => {
                inputHandler(
                  shortStartDate,
                  event.target.value.trim(),
                  selectedEndDateOption,
                );
              },
              [inputHandler, selectedEndDateOption, shortStartDate],
            )}
            mask={inputMask}
            label=""
            disabled={selectedEndDateOption !== ReportSettingEndDateOption.CustomEndDate}
          />
        </div>
      </div>
    </Styled.CustomDateRange>
  );
}

CustomDateRangeComponent.defaultProps = {
  startDateMode: DateRangeMode.ShortDate,
  endDateMode: DateRangeMode.ShortDate,
  customCalendarMode: DateRangeMode.CustomCalendar,
};

export const CustomDateRange = memo(CustomDateRangeComponent);
